// A small cache of API resources keyed by path, so screens share loads and
// switching environments shows the last answer while the new one arrives.
import React from 'react';
import { create } from 'zustand';
import { api } from '../api/client';

interface Entry<T = unknown> { data: T | null; loading: boolean; error: string | null; asOf: string | null }
interface ResourceState {
  entries: Record<string, Entry>;
  load: (path: string, force?: boolean) => Promise<void>;
  reset: () => void;
}

const EMPTY: Entry = { data: null, loading: false, error: null, asOf: null };

export const useResources = create<ResourceState>((set, get) => ({
  entries: {},
  load: async (path, force = false) => {
    const cur = get().entries[path] ?? EMPTY;
    if (cur.loading) return;
    if (!force && cur.data && cur.asOf && Date.now() - new Date(cur.asOf).getTime() < 30_000) return;
    set((s) => ({ entries: { ...s.entries, [path]: { ...cur, loading: true, error: null } } }));
    try {
      const data = await api<unknown>(path);
      set((s) => ({ entries: { ...s.entries, [path]: { data, loading: false, error: null, asOf: new Date().toISOString() } } }));
    } catch (err) {
      set((s) => ({ entries: { ...s.entries, [path]: { ...(s.entries[path] ?? EMPTY), loading: false, error: (err as Error).message } } }));
    }
  },
  reset: () => set({ entries: {} }),
}));

/** Load a resource on mount (and when the path changes); returns its entry and a reload. */
export function useResource<T>(path: string | null): Entry<T> & { reload: () => void } {
  const entry = useResources((s) => (path ? s.entries[path] : undefined)) as Entry<T> | undefined;
  const load = useResources((s) => s.load);
  React.useEffect(() => { if (path) void load(path); }, [path, load]);
  return { ...(entry ?? (EMPTY as Entry<T>)), reload: () => { if (path) void load(path, true); } };
}
